import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Play, Pause, RotateCcw, Flag, SkipForward } from "lucide-react";

const MODES = ["Stopwatch", "Countdown", "Interval", "EMOM", "AMRAP", "Rest"];

const fmt = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  const cs = Math.floor((ms % 1000) / 10);
  return { mmss: `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`, cs: String(cs).padStart(2, "0") };
};

export const TimerSuite = () => {
  const [mode, setMode] = useState("Stopwatch");
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); // ms for stopwatch
  const [laps, setLaps] = useState([]);

  // config
  const [countdownSec, setCountdownSec] = useState(60);
  const [workSec, setWorkSec] = useState(30);
  const [restSec, setRestSec] = useState(15);
  const [rounds, setRounds] = useState(8);
  const [emomMin, setEmomMin] = useState(10);
  const [amrapMin, setAmrapMin] = useState(12);

  const [remaining, setRemaining] = useState(0); // ms for countdown-based
  const [phase, setPhase] = useState("work");
  const [round, setRound] = useState(1);
  const [amrapRounds, setAmrapRounds] = useState(0);
  const raf = useRef(null);
  const last = useRef(null);

  const stop = useCallback(() => {
    setRunning(false);
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;
    last.current = null;
  }, []);

  const reset = useCallback(() => {
    stop();
    setElapsed(0);
    setLaps([]);
    setRound(1);
    setPhase("work");
    setAmrapRounds(0);
    if (mode === "Countdown") setRemaining(countdownSec * 1000);
    else if (mode === "Interval") setRemaining(workSec * 1000);
    else if (mode === "EMOM") setRemaining(60 * 1000);
    else if (mode === "AMRAP") setRemaining(amrapMin * 60 * 1000);
    else if (mode === "Rest") setRemaining(restSec * 1000);
    else setRemaining(0);
  }, [mode, countdownSec, workSec, amrapMin, restSec, stop]);

  useEffect(() => { reset(); /* eslint-disable-next-line */ }, [mode]);

  const beep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; o.start();
      g.gain.setValueAtTime(0.2, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      o.stop(ctx.currentTime + 0.25);
    } catch (e) {}
  };

  const tick = useCallback((ts) => {
    if (last.current == null) last.current = ts;
    const dt = ts - last.current;
    last.current = ts;

    if (mode === "Stopwatch") {
      setElapsed((e) => e + dt);
    } else if (mode === "AMRAP") {
      setRemaining((r) => {
        const nr = r - dt;
        if (nr <= 0) { beep(); stop(); return 0; }
        return nr;
      });
    } else {
      setRemaining((r) => {
        let nr = r - dt;
        if (nr <= 0) {
          beep();
          if (mode === "Countdown") { stop(); return 0; }
          if (mode === "Rest") { stop(); return 0; }
          if (mode === "Interval") {
            setPhase((p) => {
              const next = p === "work" ? "rest" : "work";
              if (p === "rest") {
                setRound((rd) => {
                  if (rd >= rounds) { stop(); return rd; }
                  return rd + 1;
                });
              }
              return next;
            });
            const nextDur = phase === "work" ? restSec : workSec;
            return nextDur * 1000 + nr;
          }
          if (mode === "EMOM") {
            setRound((rd) => {
              if (rd >= emomMin) { stop(); return rd; }
              return rd + 1;
            });
            return 60 * 1000 + nr;
          }
        }
        return nr;
      });
    }
    raf.current = requestAnimationFrame(tick);
  }, [mode, phase, rounds, restSec, workSec, emomMin, stop]);

  const start = () => {
    if (running) return;
    setRunning(true);
    last.current = null;
    raf.current = requestAnimationFrame(tick);
  };

  useEffect(() => () => stop(), [stop]);

  const display = mode === "Stopwatch" ? fmt(elapsed) : fmt(remaining);
  const isIntervalWork = mode === "Interval" ? phase === "work" : true;

  return (
    <div data-testid="timer-suite" className="w-full min-w-0">
      <div data-testid="timer-mode-tabs" className="flex gap-1.5 overflow-x-auto rd-scrollbar pb-2 mb-4">
        {MODES.map((m) => (
          <button
            key={m}
            data-testid={`timer-mode-${m.toLowerCase()}`}
            onClick={() => setMode(m)}
            className={`whitespace-nowrap flex-shrink-0 rounded-full px-3 py-1.5 text-xs border transition-colors ${mode === m ? "bg-[#ccff0c] text-black border-[#ccff0c]" : "bg-card text-muted-foreground border-border"}`}
          >{m}</button>
        ))}
      </div>

      {/* config */}
      {mode === "Countdown" && (
        <ConfigRow label="Seconds" value={countdownSec} setValue={(v)=>{setCountdownSec(v);}} testid="cfg-countdown" />
      )}
      {mode === "Interval" && (
        <div className="grid grid-cols-3 gap-2 mb-4">
          <ConfigRow label="Work s" value={workSec} setValue={setWorkSec} testid="cfg-work" small />
          <ConfigRow label="Rest s" value={restSec} setValue={setRestSec} testid="cfg-rest" small />
          <ConfigRow label="Rounds" value={rounds} setValue={setRounds} testid="cfg-rounds" small />
        </div>
      )}
      {mode === "EMOM" && <ConfigRow label="Minutes" value={emomMin} setValue={setEmomMin} testid="cfg-emom" />}
      {mode === "AMRAP" && <ConfigRow label="Minutes" value={amrapMin} setValue={setAmrapMin} testid="cfg-amrap" />}
      {mode === "Rest" && <ConfigRow label="Seconds" value={restSec} setValue={setRestSec} testid="cfg-restonly" />}

      {/* display */}
      <div className={`rounded-2xl border p-6 text-center mb-4 ${isIntervalWork ? "border-[#ccff0c]/30 bg-[#ccff0c]/5" : "border-border bg-card"}`}>
        {(mode === "Interval") && <p className={`text-sm uppercase tracking-wide mb-1 ${phase === "work" ? "text-[#ccff0c]" : "text-muted-foreground"}`}>{phase} &middot; Round {round}/{rounds}</p>}
        {mode === "EMOM" && <p className="text-sm uppercase tracking-wide text-[#ccff0c] mb-1">Minute {round}/{emomMin}</p>}
        {mode === "AMRAP" && <p className="text-sm uppercase tracking-wide text-[#ccff0c] mb-1">Rounds: {amrapRounds}</p>}
        <div className="flex items-baseline justify-center font-mono font-bold tabular-nums leading-none">
          <span data-testid="timer-display" className="text-5xl sm:text-6xl">{display.mmss}</span>
          {mode === "Stopwatch" && <span className="text-2xl text-muted-foreground w-0 overflow-visible whitespace-nowrap">.{display.cs}</span>}
        </div>
      </div>

      {/* controls */}
      <div className="flex items-center justify-center gap-3">
        {!running ? (
          <Button data-testid="timer-start-button" onClick={start} className="h-12 px-8 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-semibold"><Play className="h-5 w-5 mr-1" /> Start</Button>
        ) : (
          <Button data-testid="timer-pause-button" onClick={stop} className="h-12 px-8 bg-secondary border border-border text-foreground"><Pause className="h-5 w-5 mr-1" /> Pause</Button>
        )}
        <Button data-testid="timer-reset-button" onClick={reset} variant="ghost" className="h-12 px-4 text-muted-foreground"><RotateCcw className="h-5 w-5" /></Button>
        {mode === "Stopwatch" && (
          <Button data-testid="timer-lap-button" onClick={() => setLaps((l) => [...l, elapsed])} variant="ghost" className="h-12 px-4 text-muted-foreground"><Flag className="h-5 w-5" /></Button>
        )}
        {mode === "AMRAP" && running && (
          <Button data-testid="amrap-round" onClick={() => setAmrapRounds((r) => r + 1)} className="h-12 px-4 bg-secondary border border-border text-foreground">+1 Round</Button>
        )}
        {mode === "Rest" && running && (
          <Button data-testid="skip-rest" onClick={stop} variant="ghost" className="h-12 px-4 text-muted-foreground"><SkipForward className="h-5 w-5" /></Button>
        )}
      </div>

      {mode === "Stopwatch" && laps.length > 0 && (
        <div className="mt-4 max-h-40 overflow-y-auto">
          {laps.map((l, i) => (
            <div key={i} className="flex justify-between border-b border-border py-1.5 text-sm">
              <span className="text-muted-foreground">Lap {i + 1}</span>
              <span className="font-mono">{fmt(l).mmss}.{fmt(l).cs}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ConfigRow = ({ label, value, setValue, testid, small }) => (
  <div className={small ? "" : "mb-4"}>
    <label className="text-xs text-muted-foreground">{label}</label>
    <Input
      data-testid={testid}
      type="number"
      value={value}
      onChange={(e) => setValue(parseInt(e.target.value) || 0)}
      className="bg-card border-border h-10 mt-1"
    />
  </div>
);
